import { TopBar, type TopBarProps } from '../TopBar'
import { Sidebar, type SidebarProps } from '../Sidebar'
import { useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import styles from './MainLayout.module.scss'

export interface MainLayoutProps {
  /**
   * Props du TopBar
   */
  topBarProps: TopBarProps

  /**
   * Props de la Sidebar
   */
  sidebarProps: SidebarProps

  /**
   * Contenu principal
   */
  children: React.ReactNode

  /**
   * Classes CSS additionnelles pour le container
   */
  className?: string

  /**
   * Classes CSS pour la zone de contenu principal
   */
  contentClassName?: string

  /**
   * Masquer la sidebar (fullscreen)
   */
  hideSidebar?: boolean

  /**
   * Sidebar collapsée par défaut
   */
  defaultCollapsed?: boolean
}

/**
 * MainLayout - Layout principal de l'application
 *
 * Structure Carbon UI Shell :
 * ```
 * ┌────────────────────────────────────────────────────────────┐
 * │                    HEADER (48px)                           │
 * ├──────────┬─────────────────────────────────────────────────┤
 * │ SIDENAV  │                                                 │
 * │ (256px)  │         MAIN CONTENT AREA                       │
 * │          │                                                 │
 * └──────────┴─────────────────────────────────────────────────┘
 * ```
 */
export function MainLayout({
  topBarProps,
  sidebarProps,
  children,
  className,
  contentClassName,
  hideSidebar = false,
  defaultCollapsed = false,
}: MainLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(defaultCollapsed)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const location = useLocation()
  const mainContentRef = useRef<HTMLDivElement>(null)

  // Détecter la taille d'écran
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1056 // Breakpoint lg Carbon
      setIsMobile(mobile)
      // Auto-collapse sur tablette (md)
      if (window.innerWidth < 1056 && window.innerWidth >= 672) {
        setSidebarCollapsed(true)
      } else if (window.innerWidth >= 1056) {
        setSidebarCollapsed(defaultCollapsed)
      }
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [defaultCollapsed])

  // Fermer sidebar mobile lors du changement de route
  // ET reset scroll du contenu principal
  useEffect(() => {
    setMobileSidebarOpen(false)
    if (mainContentRef.current) {
      mainContentRef.current.scrollTo(0, 0)
    }
  }, [location.pathname])

  const handleToggleSidebar = () => {
    if (isMobile) {
      setMobileSidebarOpen(!mobileSidebarOpen)
    } else {
      setSidebarCollapsed(!sidebarCollapsed)
    }
  }

  return (
    <div className={`${styles.layout} ${className || ''}`}>
      {/* Skip link - Accessibilité navigation clavier */}
      <a
        href="#main-content"
        className={styles.skipLink}
      >
        Aller au contenu principal
      </a>
      {/* HEADER */}
      <TopBar
        {...topBarProps}
        sidebarCollapsed={sidebarCollapsed}
        onToggleSidebar={handleToggleSidebar}
      />

      {/* BODY (Sidebar + Content) */}
      <div className={styles.body}>
        {!hideSidebar && (
          <>
            {/* MOBILE: Overlay + Sidebar slide-over */}
            {isMobile && (
              <>
                  <div
                  className={`${styles.overlay} ${mobileSidebarOpen ? styles.overlayVisible : ''}`}
                    onClick={() => setMobileSidebarOpen(false)}
                  />
                <div
                  className={`${styles.sidebarMobile} ${mobileSidebarOpen ? styles.sidebarMobileOpen : ''}`}
                >
                  <Sidebar
                    {...sidebarProps}
                    collapsed={false}
                    onToggleCollapsed={() => setMobileSidebarOpen(false)}
                  />
                </div>
              </>
            )}

            {/* DESKTOP: Static Sidebar */}
            {!isMobile && (
                <Sidebar
                  {...sidebarProps}
                  collapsed={sidebarCollapsed}
                  onToggleCollapsed={() => setSidebarCollapsed(!sidebarCollapsed)}
                />
            )}
          </>
        )}

        {/* Main Content Area */}
        <main
          id="main-content"
          ref={mainContentRef}
          className={`${styles.main} ${contentClassName || ''}`}
          tabIndex={-1}
        >
          {children}
        </main>
      </div>
    </div>
  )
}
